// IMOTEpS — Centre de recherche / Chapitre 1
// Auto-split from chapters.js (Feb 2026 refactor)

export const chapter01 = {
  id: 1,
  slug: "capsulite-retractile",
  color: "#2D4B8C",
  title: "Capsulite rétractile",
  subtitle: "Épaule gelée — un processus biologique qu'on accompagne, pas une maladie qu'on guérit",
  region: "Épaule",
  readingMin: 22,
  summary:
    "Synovite inflammatoire suivie d'une fibrose capsulaire avec rétraction du récessus axillaire et du ligament coraco-huméral. Diagnostic clinique (limitation passive de la rotation externe), résolution spontanée en 1-3 ans, infiltration intra-articulaire de corticoïde en phase inflammatoire, capsulotomie arthroscopique réservée aux formes réfractaires après 18-24 mois.",
  sections: [
    {
      heading: "1. Clarifier les termes",
      body: "« Capsulite rétractile » est le terme français de référence — précis anatomiquement. « Frozen shoulder » (Codman 1934) décrit le symptôme. « Capsulite adhésive » (Neviaser 1945) est le terme histologique utilisé en littérature internationale. À éviter : « algodystrophie de l'épaule » (mécanisme neurologique différent — SDRC) et « périarthrite scapulo-humérale » (Duplay 1872, trop vague, obsolète).",
    },
    {
      heading: "2. Capsulite idiopathique vs secondaire",
      body: "Idiopathique (primitive) : femme 40-60 ans, terrain anxieux, déclenchement insidieux, parfois après choc affectif. Facteurs favorisants : diabète (×3), hypothyroïdie. Secondaire : post-traumatique (chute, contusion), post-fracture, post-chirurgicale, neurologique (hémiplégie, Parkinson), cardiaque (post-IDM), ou tumorale (Pancoast-Tobias).",
    },
    {
      heading: "3. Physiopathologie",
      body: "Synovite inflammatoire (IL-1, IL-6, TNF-α) puis fibrose progressive — myofibroblastes, collagène I et III en excès. La capacité capsulaire passe de 20-30 ml à parfois moins de 5 ml. L'axe cerveau-système immunitaire (Frontiers Physiology 2024) propose un modèle où stress chronique et inflammation de bas grade constituent le terrain de déclenchement — cohérent avec le profil clinique de la patiente stressée à hautes responsabilités.",
    },
    {
      heading: "4. Phases évolutives — pas de règle universelle",
      list: [
        "Phase inflammatoire (Freezing) : quelques semaines à 12 mois, douleur dominante (souvent nocturne), épaule parfois inexaminable",
        "Phase de blocage (Frozen / Thawing) : 6 mois à 3 ans, raideur dominante, limitation passive de la rotation externe en signe cardinal",
        "Pas de chronologie stricte — chaque patient a son propre rythme",
      ],
    },
    {
      heading: "5. Le piège chirurgical — l'erreur fréquente",
      body: "Une capsulite associée à une lésion structurelle (calcification, fissure de coiffe) passe inaperçue à l'IRM. Le patient est opéré, l'épaule reste douloureuse 10-14 mois, puis s'améliore — on attribue la guérison à la chirurgie alors qu'il s'agit de la résolution spontanée de la capsulite. Règle absolue : avant toute chirurgie élective, mesurer la rotation externe passive en décubitus dorsal coude au corps. Une diminution > 10-15° vs le côté sain contre-indique la chirurgie élective jusqu'à résolution.",
    },
    {
      heading: "6. Diagnostic clinique avant tout",
      body: "Aucun examen d'imagerie ne « voit » directement la capsulite. La radiographie élimine arthrose et tumeur. L'IRM peut montrer un épaississement du ligament coraco-huméral (Se 59 %, Sp 86 %) — signes subtils. Une IRM normale n'élimine pas une capsulite, et une IRM montrant un « épaississement » sans limitation passive de RE n'est pas une capsulite.",
    },
    {
      heading: "7. Traitements — par niveau de preuve",
      list: [
        "Infiltration intra-articulaire de corticoïde — traitement de choix en phase inflammatoire (preuve solide court terme)",
        "Hydrodilatation — l'effet semble lié au corticoïde plutôt qu'à la distension mécanique",
        "Kinésithérapie : accompagner, expliquer, rassurer — JAMAIS forcer en phase inflammatoire",
        "Acide hyaluronique — option de 2e ligne chez le diabétique",
        "Mobilisation sous AG — risque de fracture, contre-indication chez l'ostéoporotique",
        "Capsulotomie arthroscopique — formes sévères réfractaires après 18-24 mois minimum",
      ],
    },
    {
      heading: "8. Réflexion personnelle",
      body: "Dans mon expérience de 30 ans, la capsulite touche très souvent des femmes qui portent beaucoup — professionnellement, familièrement, émotionnellement. Le corps dit ce que la tête ne peut plus contenir. Expliquer cela au patient, le faire accepter, c'est souvent le premier pas vers la guérison. Le rôle du soignant est d'accompagner ce processus, d'en réduire la durée et la souffrance, et surtout d'éviter les erreurs qui l'aggravent.",
    },
  ],
  references: [
    { id: 1, text: "Millar NL, et al. Frozen shoulder. Nat Rev Dis Primers. 2022;8(1):59." },
    { id: 2, text: "Sun Y, et al. Steroid injection vs physiotherapy for frozen shoulder: meta-analysis. AJSM. 2017." },
    { id: 3, text: "Kitridis D, et al. Intra-articular corticosteroid injection for frozen shoulder. AJSM. 2019." },
    { id: 4, text: "Erber B, et al. MRI signs of adhesive capsulitis. 2024." },
    { id: 5, text: "Brindisino F, et al. Psychological factors in frozen shoulder. Cureus. 2023." },
  ],
};
